import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { Clapperboard, Copy, Download, Loader2, ChevronDown, Wand2, Image as ImageIcon } from "lucide-react";
import { api, errMsg } from "@/lib/api";
import { Button } from "@/components/ui/button";

const copy = (t, what) => navigator.clipboard.writeText(t).then(() => toast.success(`${what} copied`));
const shotText = (s, i) => `Shot ${i + 1} (${s.seconds}s) · Image: ${s.image || "none"}\n${s.description}\nOn-screen text: ${s.text}`;
const saveBlob = (path, name) => api.get(path, { responseType: "blob" }).then((r) => {
  const a = document.createElement("a"); a.href = URL.createObjectURL(r.data); a.download = name; a.click();
}).catch((e) => toast.error(errMsg(e)));

const ShotImage = ({ base, name, testId }) => {
  const [url, setUrl] = useState("");
  useEffect(() => {
    if (!name) return undefined;
    let u = "";
    api.get(`${base}/image/${encodeURIComponent(name)}`, { responseType: "blob" }).then((r) => { u = URL.createObjectURL(r.data); setUrl(u); }).catch(() => setUrl(""));
    return () => u && URL.revokeObjectURL(u);
  }, [base, name]);
  return (
    <div className="w-20 shrink-0" data-testid={testId}>
      <div className="aspect-[9/16] bg-[#F4EFE6] border border-[#E2D9CC] overflow-hidden flex items-center justify-center">
        {url ? <img src={url} alt={name} className="w-full h-full object-cover" /> : <ImageIcon className="w-5 h-5 text-[#C9BFB2]" />}
      </div>
      <p className="text-[10px] text-[#8C827A] mt-1 truncate" title={name}>{name || "no image"}</p>
    </div>
  );
};

const Shot = ({ s, i, base, testId }) => (
  <li className="border border-[#E2D9CC] bg-white p-3 text-sm flex gap-4" data-testid={testId}>
    <ShotImage base={base} name={s.image} testId={`${testId}-image`} />
    <div className="flex-1 min-w-0 space-y-2">
      <div className="flex items-center text-xs">
        <span className="font-mono-accent uppercase tracking-wider text-[#C59B27]">Shot {i + 1} · {s.seconds}s</span>
        <button data-testid={`${testId}-copy`} onClick={() => copy(shotText(s, i), `Shot ${i + 1}`)} className="ml-auto inline-flex items-center gap-1 text-[#3B1B48] hover:text-[#C59B27]"><Copy className="w-3.5 h-3.5" />Copy</button>
      </div>
      <p data-testid={`${testId}-description`}><span className="eyebrow mr-2">Description</span>{s.description}</p>
      <p data-testid={`${testId}-text`}><span className="eyebrow mr-2">On-screen text</span><span className="font-semibold text-[#3B1B48]">“{s.text}”</span></p>
    </div>
  </li>
);

const Video = ({ v, len, testId, base }) => (
  <div className="space-y-3" data-testid={testId}>
    <div className="flex flex-wrap items-baseline gap-3">
      <p className="font-display text-lg text-[#3B1B48]">{v.title}</p>
      <span className="text-xs text-[#8C827A]">{v.total}s · {v.shots.length} shots</span>
      <button data-testid={`${testId}-copy-all`} onClick={() => copy([`${len}s short: ${v.title}`, ...v.shots.map(shotText), `Voiceover script: ${v.voiceover}`].join("\n\n"), "Whole short")} className="ml-auto text-xs inline-flex items-center gap-1 text-[#3B1B48] hover:text-[#C59B27]"><Copy className="w-3.5 h-3.5" />Copy whole short</button>
    </div>
    <ol className="space-y-2">{v.shots.map((s, i) => <Shot key={i} s={s} i={i} base={base} testId={`${testId}-shot-${i + 1}`} />)}</ol>
    <div className="border border-[#E2D9CC] bg-[#F4EFE6] p-3 text-sm" data-testid={`${testId}-vo`}>
      <div className="flex items-center mb-1"><span className="eyebrow">Voiceover script</span><span className="ml-3 text-xs text-[#8C827A]">{v.voiceover.split(/\s+/).filter(Boolean).length} words</span><button data-testid={`${testId}-vo-copy`} onClick={() => copy(v.voiceover, "Voiceover script")} className="ml-auto text-xs inline-flex items-center gap-1 text-[#3B1B48] hover:text-[#C59B27]"><Copy className="w-3.5 h-3.5" />Copy</button></div>
      <p>{v.voiceover}</p>
    </div>
  </div>
);

const Product = ({ kind, refId, p, reload }) => {
  const [open, setOpen] = useState(false);
  const [len, setLen] = useState("20");
  const d = p.prompts;
  const tid = `video-${p.key}`;
  const base = `/video-prompts/${kind}/${refId}/${p.key}`;
  const fname = p.name.replace(/\W+/g, "_");
  const write = async () => {
    try { await api.post(base); toast.success(`Writing the shorts for ${p.name}, about a minute`); reload(); } catch (e) { toast.error(errMsg(e)); }
  };
  const writing = d?.status === "writing";
  const ready = d?.status === "ready" && d.videos?.["20"]?.shots?.[0]?.description !== undefined;
  return (
    <div className="border-b border-[#E2D9CC] last:border-0" data-testid={tid}>
      <div className="flex flex-wrap items-center gap-3 py-3">
        <button onClick={() => ready && setOpen(!open)} disabled={!ready} data-testid={`${tid}-toggle`} className="flex items-center gap-2 text-left mr-auto disabled:cursor-default">
          <ChevronDown className={`w-4 h-4 text-[#3B1B48] transition-transform ${open ? "" : "-rotate-90"} ${ready ? "" : "opacity-20"}`} />
          <span className="text-[#1C181D]">{p.name}</span>{p.price_aud ? <span className="text-xs text-[#8C827A]">A${p.price_aud}</span> : null}
          {ready && <span className="text-xs text-[#2D6A4F]">20s · 40s · 60s ready</span>}
        </button>
        {ready && <Button size="sm" variant="outline" data-testid={`${tid}-refs`} onClick={() => saveBlob(`${base}/references.zip`, `${fname}_images.zip`)} className="rounded-none h-8"><ImageIcon className="w-3.5 h-3.5 mr-1" />Images</Button>}
        {ready && <Button size="sm" variant="outline" data-testid={`${tid}-download`} onClick={() => saveBlob(`${base}/text`, `${fname}_shorts.txt`)} className="rounded-none h-8"><Download className="w-3.5 h-3.5 mr-1" />.txt</Button>}
        <Button size="sm" data-testid={`${tid}-write`} onClick={write} disabled={writing} className="rounded-none h-8">{writing ? <Loader2 className="w-3.5 h-3.5 mr-1 animate-spin" /> : <Wand2 className="w-3.5 h-3.5 mr-1" />}{writing ? "Writing…" : ready ? "Rewrite" : "Write shorts"}</Button>
      </div>
      {d?.status === "failed" && <p className="text-xs text-red-700 pb-3 pl-6 -mt-1 line-clamp-2" data-testid={`${tid}-error`}>Couldn't write them: {d.error}</p>}
      {open && ready && (
        <div className="pb-5 pl-6 space-y-4">
          <div className="flex gap-2">{["20", "40", "60"].map((k) => <button key={k} data-testid={`${tid}-len-${k}`} onClick={() => setLen(k)} className={`px-3 py-1.5 text-sm border transition-colors ${len === k ? "bg-[#3B1B48] text-[#FAF7F2] border-[#3B1B48]" : "border-[#E2D9CC] hover:border-[#3B1B48]"}`}>{k === "60" ? "1 minute" : `${k} seconds`}</button>)}</div>
          <Video v={d.videos[len]} len={len} testId={`${tid}-v${len}`} base={base} />
        </div>
      )}
    </div>
  );
};

export const VideoPrompts = ({ kind, refId, compact = false }) => {
  const [items, setItems] = useState(null);
  const load = useCallback(() => api.get(`/video-prompts/${kind}/${refId}`).then((r) => setItems(r.data)).catch(() => setItems([])), [kind, refId]);
  useEffect(() => { load(); }, [load]);
  const busy = items?.some((p) => p.prompts?.status === "writing");
  useEffect(() => { if (!busy) return undefined; const t = setInterval(load, 4000); return () => clearInterval(t); }, [busy, load]);
  if (!items?.length) return null;
  const writeAll = async () => {
    if (!window.confirm(`Write the shorts for all ${items.length} products? Each uses a little AI credit.`)) return;
    try { await api.post(`/video-prompts/${kind}/${refId}`); toast.success(`Writing shorts for ${items.length} products, two at a time`); load(); } catch (e) { toast.error(errMsg(e)); }
  };
  return (
    <section className={compact ? "" : "border border-[#E2D9CC] bg-[#FAF7F2] p-6"} data-testid={`video-prompts-${kind}`}>
      {!compact && <div className="flex flex-wrap items-start gap-4 mb-3">
        <div className="mr-auto">
          <p className="eyebrow mb-1 flex items-center gap-2"><Clapperboard className="w-4 h-4" strokeWidth={1.5} />Video sales shorts</p>
          <p className="text-sm text-[#8C827A] max-w-3xl">20 second, 40 second and 1 minute vertical shorts for Instagram Reels, YouTube Shorts and Facebook, one set per product. Each short has 5 shots, each with its guide image, a description and the on-screen text, plus the voiceover script.{kind === "reloc" ? " Written automatically when a relocation guide finishes." : ""}</p>
        </div>
        {items.length > 1 && <Button data-testid={`video-prompts-write-all-${kind}`} onClick={writeAll} disabled={busy} className="rounded-none"><Wand2 className="w-4 h-4 mr-2" />Write all {items.length}</Button>}
      </div>}
      {items.map((p) => <Product key={p.key} kind={kind} refId={refId} p={p} reload={load} />)}
    </section>
  );
};
